import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setAccessToken, setUnauthorizedHandler } from './api.js';

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: 'passenger' | 'driver' | 'admin';
  hasPhoto?: boolean;
  rating?: number | null;
  ratingCount?: number;
}

interface LoginArgs {
  email: string;
  password: string;
  totp?: string;
}

interface SignupArgs {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: 'passenger' | 'driver';
}

interface AuthValue {
  user: User | null;
  loading: boolean;
  login: (args: LoginArgs) => Promise<void>;
  signup: (args: SignupArgs) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({
  children,
  expectedRole,
}: {
  children: ReactNode;
  /** cada domínio serve um papel; entrar com o papel errado é erro de app */
  expectedRole: User['role'];
}) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const me = await api<User>('/api/auth/me');
    if (me.role !== expectedRole) {
      await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
      setAccessToken(null);
      setUser(null);
      throw new Error('esta conta não é deste aplicativo');
    }
    setUser(me);
  }, [expectedRole]);

  // Sessão sobrevive ao refresh da página pelo cookie HttpOnly: na
  // montagem tentamos renovar e só então buscamos o usuário.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    let cancelled = false;

    (async () => {
      try {
        await loadMe();
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  const login = useCallback(
    async (args: LoginArgs) => {
      const res = await api<{ accessToken: string; user: User }>('/api/auth/login', {
        body: args,
        skipRefresh: true,
      });
      setAccessToken(res.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const signup = useCallback(
    async (args: SignupArgs) => {
      const res = await api<{ accessToken: string; user: User }>('/api/auth/signup', {
        body: args,
        skipRefresh: true,
      });
      setAccessToken(res.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ user, loading, login, signup, logout, refresh: loadMe }),
    [user, loading, login, signup, logout, loadMe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de AuthProvider');
  return ctx;
}
